import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { components } from '@/lib/api/schema'

/**
 * The platform console's data (S8-12 to S8-17): Ringout's own people, across every account.
 * Pages call these, never `api` directly. Everything is under the `['admin']` key so one
 * invalidation refreshes the console after an action.
 *
 * Nothing here carries a caller's words: the API sends counts, states and timestamps only.
 */
type Schemas = components['schemas']

export type PlatformMe = Schemas['PlatformMe']
export type Overview = Schemas['Overview']
export type AccountPage = Schemas['AccountPage']
export type AccountSummary = Schemas['PlatformAccountSummary']
export type AccountDetail = Schemas['AccountDetail']
export type LeadPage = Schemas['LeadPage']
export type Billing = Schemas['Billing']
export type CallPage = Schemas['CallPage']
export type CallItem = Schemas['CallItem']
export type Jobs = Schemas['Jobs']
export type AccountStatus = Schemas['PlatformAccountSummary']['status']
export type LeadKind = 'demo' | 'contact' | 'volume' | 'partner' | 'calculator'

export const ADMIN_KEY = ['admin'] as const
export const ADMIN_PAGE_SIZE = 25

export class AdminError extends Error {
  status: number
  code?: string
  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'AdminError'
    this.status = status
    this.code = code
  }
}

export function adminErrorMessage(code: string | undefined, status: number): string {
  switch (code) {
    case 'platform_required':
    case 'not_found':
      return 'The platform console is not available to you.'
    case 'platform_admin_required':
      return 'Only a platform admin can do that.'
    case 'account_not_found':
      return 'That account no longer exists.'
    case 'already_in_that_state':
      return 'The account is already in that state. Reload to see its current status.'
    case 'account_closed':
      return 'A closed account cannot be changed from the console.'
    case 'not_suspended':
      return 'The account is not suspended.'
    case 'job_not_found':
      return 'That job no longer exists.'
    case 'job_done':
      return 'That job already finished.'
    case 'job_not_dead':
      return 'That job is not dead; it will run on its own.'
    default:
      return status === 422
        ? 'Give a reason of at least three characters.'
        : 'Something went wrong. Please try again.'
  }
}

function adminError(error: unknown, status: number) {
  const code = (error as { error?: { code?: string } } | undefined)?.error?.code
  return new AdminError(adminErrorMessage(code, status), status, code)
}

/**
 * Whether this person may use the console. A 403 (no grant) and a 404 (console off) are the
 * same answer to them, by design: neither says which. Resolves to `forbidden` rather than
 * throwing, so the guard can show a calm screen, not an error boundary. A 401 means the
 * session ended; the session guard above this handles it.
 */
export type AdminAccess = { kind: 'ok'; me: PlatformMe } | { kind: 'forbidden' }

export const adminMeQuery = queryOptions({
  queryKey: [...ADMIN_KEY, 'me'],
  queryFn: async (): Promise<AdminAccess> => {
    const { data, response } = await api.GET('/api/admin/me')
    if (data) return { kind: 'ok', me: data }
    if (response.status === 403 || response.status === 404) return { kind: 'forbidden' }
    throw new AdminError('Could not check your access', response.status)
  },
  retry: false,
  staleTime: 60_000,
})

export const adminOverviewQuery = queryOptions({
  queryKey: [...ADMIN_KEY, 'overview'],
  queryFn: async ({ signal }) => {
    const { data, error, response } = await api.GET('/api/admin/overview', { signal })
    if (!data) throw adminError(error, response.status)
    return data
  },
  // No polling: every read writes a permanent audit row (`platform_audit` is never swept), so a tab
  // left open must not write one every few seconds. Refetching on window focus keeps it fresh.
})

export type AccountsFilter = { q: string; status: AccountStatus | ''; offset: number }

export const adminAccountsQuery = ({ q, status, offset }: AccountsFilter) =>
  queryOptions({
    queryKey: [...ADMIN_KEY, 'accounts', q, status, offset],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/admin/accounts', {
        params: {
          query: { q: q || undefined, status: status || undefined, limit: ADMIN_PAGE_SIZE, offset },
        },
        signal,
      })
      if (!data) throw adminError(error, response.status)
      return data
    },
    placeholderData: keepPreviousData,
  })

export const adminAccountQuery = (id: string) =>
  queryOptions({
    queryKey: [...ADMIN_KEY, 'account', id],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/admin/accounts/{account_id}', {
        params: { path: { account_id: id } },
        signal,
      })
      if (!data) throw adminError(error, response.status)
      return data
    },
  })

export const adminLeadsQuery = ({ kind, offset }: { kind: LeadKind | ''; offset: number }) =>
  queryOptions({
    queryKey: [...ADMIN_KEY, 'leads', kind, offset],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/admin/leads', {
        params: { query: { kind: kind || undefined, limit: ADMIN_PAGE_SIZE, offset } },
        signal,
      })
      if (!data) throw adminError(error, response.status)
      return data
    },
    placeholderData: keepPreviousData,
  })

export const adminBillingQuery = queryOptions({
  queryKey: [...ADMIN_KEY, 'billing'],
  queryFn: async ({ signal }) => {
    const { data, error, response } = await api.GET('/api/admin/billing', { signal })
    if (!data) throw adminError(error, response.status)
    return data
  },
})

export type CallsFilter = {
  view: 'all' | 'live' | 'urgent' | 'attention'
  accountId: string
  offset: number
}

export const adminCallsQuery = ({ view, accountId, offset }: CallsFilter) =>
  queryOptions({
    queryKey: [...ADMIN_KEY, 'calls', view, accountId, offset],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/admin/calls', {
        params: {
          query: {
            live: view === 'live' || undefined,
            urgent: view === 'urgent' || undefined,
            attention: view === 'attention' || undefined,
            account_id: accountId || undefined,
            limit: ADMIN_PAGE_SIZE,
            offset,
          },
        },
        signal,
      })
      if (!data) throw adminError(error, response.status)
      return data
    },
    placeholderData: keepPreviousData,
    // One audit row per refetch, so the live view polls once a minute, not every few seconds.
    refetchInterval: view === 'live' ? 60_000 : false,
  })

export const adminJobsQuery = queryOptions({
  queryKey: [...ADMIN_KEY, 'jobs'],
  queryFn: async ({ signal }) => {
    const { data, error, response } = await api.GET('/api/admin/jobs', { signal })
    if (!data) throw adminError(error, response.status)
    return data
  },
  // No polling: every read writes a permanent audit row (`platform_audit` is never swept), so a tab
  // left open must not write one every few seconds. Refetching on window focus keeps it fresh.
})

// --- actions (platform_admin only) ---------------------------------------------------------

/** Suspends an active account. The reason is kept in the audit log. */
export async function suspendAccount(id: string, reason: string) {
  const { data, error, response } = await api.POST('/api/admin/accounts/{account_id}/suspend', {
    params: { path: { account_id: id } },
    body: { reason },
  })
  if (!data) throw adminError(error, response.status)
  return data
}

/** Lifts a suspension. A closed account is never reopened from here. */
export async function reactivateAccount(id: string, reason: string) {
  const { data, error, response } = await api.POST('/api/admin/accounts/{account_id}/reactivate', {
    params: { path: { account_id: id } },
    body: { reason },
  })
  if (!data) throw adminError(error, response.status)
  return data
}

/** Gives a dead job a fresh set of attempts. */
export async function retryJob(id: number) {
  const { data, error, response } = await api.POST('/api/admin/jobs/{job_id}/retry', {
    params: { path: { job_id: id } },
  })
  if (!data) throw adminError(error, response.status)
  return data
}
